// @ts-nocheck
import React from 'react';
import { 
  Box, 
  Drawer, 
  FormControl, 
  IconButton, 
  InputLabel, 
  MenuItem, 
  Select, 
  Typography 
} from '@mui/material';
import { FaTags } from 'react-icons/fa';

interface ChannelOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface VariationOption {
  value: string;
  label: string;
}

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;

  // Forecast selection
  forecastType: string;
  handleForecastTypeChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  showWindPowerOption: boolean;

  // Channel and variation controls
  forecastChannel: string;
  handleForecastChannelChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  channelOptions: ChannelOption[];

  forecastVariation: string;
  handleForecastVariationChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  variationOptions: VariationOption[];

  // Tooltip toggle
  showAllTooltips: boolean;
  setShowAllTooltips: (show: boolean) => void;

  // Time format selection
  localTimeBool: boolean;
  setUseLocalTime: (useLocal: boolean) => void;

  // Model data
  processedModelData: any;
  dataFetched: boolean;
  assetItems: any[];

  // Filtering
  assetGroup?: any;
  filterForecastTypes: (forecastTypeKey: string) => boolean;
}

/**
 * Mobile drawer component with forecast settings and controls
 */
const MobileDrawer: React.FC<MobileDrawerProps> = ({
  open,
  onClose,
  forecastType,
  handleForecastTypeChange,
  showWindPowerOption,
  forecastChannel,
  handleForecastChannelChange,
  channelOptions,
  forecastVariation,
  handleForecastVariationChange,
  variationOptions,
  showAllTooltips,
  setShowAllTooltips,
  localTimeBool,
  setUseLocalTime,
  processedModelData,
  dataFetched,
  assetItems,
  processedModelDetails,
  assetGroup,
  filterForecastTypes
}) => {
  // Handler for forecast type changes that also closes the drawer
  const handleTypeChange = (e) => {
    handleForecastTypeChange(e);
    onClose();
  };
  
  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
    >
      <Box sx={{ width: 250, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Forecast Settings</Typography>

        <FormControl variant="outlined" size="small" sx={{ width: '100%', mb: 2 }}>
          <InputLabel>Forecast Type</InputLabel>
          <Select
            value={forecastType}
            onChange={handleTypeChange}
            label="Forecast Type"
          >
            {/* Processed Models section */}
            {/* Erode AI Forecasts header */}
            {processedModelDetails && processedModelDetails?.length > 0 && 
              <MenuItem disabled sx={{ fontWeight: 'bold', color: '#082b61', mt: 1 }}>— Erode AI Forecasts —</MenuItem>
            }
            
            {/* Processed model options */}
            {processedModelDetails && 
              // 1) Filter to one item per unique routeShort
              processedModelDetails.filter((model, idx, all) =>
                all.findIndex(m => m.routeShort === model.routeShort) === idx
              )
              // 2) Map each unique model into a <MenuItem>
              .map(model => {
                const key = model.routeShort;
                const hasProcessedModel = assetItems.some(asset =>
                  asset?.processedModels?.items?.some(pm => pm.routeShort === key)
                );
                return (
                  <MenuItem
                    key={`processed-${key}`}
                    value={`processed-${key}`}
                    disabled={!hasProcessedModel}
                    sx={!hasProcessedModel ? { color: 'rgba(0, 0, 0, 0.38)' } : {}}
                  >
                    {model.label || key}
                  </MenuItem>
                );
              })}
            <MenuItem disabled sx={{ fontWeight: 'bold', color: '#082b61' }}>— Forecast Types —</MenuItem>
            {filterForecastTypes('precipitation') && <MenuItem value="precipitation">Precipitation</MenuItem>}
            {filterForecastTypes('wind10m') && <MenuItem value="wind10m">Wind Speed (10m)</MenuItem>}
            {filterForecastTypes('wind80m') && <MenuItem value="wind80m">Wind Speed (80m)</MenuItem>}
            {showWindPowerOption && filterForecastTypes('windPower') && <MenuItem value="windPower">Wind Power (MW)</MenuItem>}
            {filterForecastTypes('temperature') && <MenuItem value="temperature">Temperature</MenuItem>}
            {filterForecastTypes('humidity') && <MenuItem value="humidity">Humidity</MenuItem>}
            {filterForecastTypes('solarS') && <MenuItem value="solarS">Solar Radiation (Short)</MenuItem>}
            {filterForecastTypes('solarL') && <MenuItem value="solarL">Solar Radiation (Long)</MenuItem>}
            {filterForecastTypes('soilMoisture') && <MenuItem value="soilMoisture">Soil Moisture</MenuItem>}
            {filterForecastTypes('snowWater') && <MenuItem value="snowWater">Snow Water Equivalent</MenuItem>}           
          </Select>
        </FormControl>

        {/* Channel dropdown */}
        {forecastType && channelOptions.length > 0 && (
          <FormControl variant="outlined" size="small" sx={{ width: '100%', mb: 2 }}>
            <InputLabel>Channel</InputLabel>
            <Select
              value={forecastChannel}
              onChange={handleForecastChannelChange}
              label="Channel"
            >
              {channelOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Variation dropdown - not shown for processed models */}
        {variationOptions.length > 0 && dataFetched && !forecastType.startsWith('processed-') && (
          <FormControl variant="outlined" size="small" sx={{ width: '100%', mb: 2 }}>
            <InputLabel>Variation</InputLabel>
            <Select
              value={forecastVariation}
              onChange={handleForecastVariationChange}
              label="Variation"
            >
              {variationOptions.map((variation) => (
                <MenuItem key={variation.value} value={variation.value}>
                  {variation.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <Typography variant="body2">Show All Tooltips</Typography>
          <IconButton
            size="small"
            onClick={() => setShowAllTooltips(!showAllTooltips)}
            color={showAllTooltips ? "primary" : "default"}
            sx={{ ml: 1 }}
          >
            <FaTags />
          </IconButton>
        </Box>

        <FormControl variant="outlined" size="small" sx={{ width: '100%', mt: 3 }}>
          <InputLabel>Time Format</InputLabel>
          <Select
            value={localTimeBool ? "local" : "utc"}
            onChange={(e) => setUseLocalTime(e.target.value === "local")}
            label="Time Format"
          >
            <MenuItem value="local">Local Time</MenuItem>
            <MenuItem value="utc">UTC Time</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Drawer>
  );
};

export default MobileDrawer;